import { FC, PropsWithChildren, useEffect, KeyboardEvent } from 'react';

import {
  setBodyBackgroundColor,
  SpinnerIconContainer,
  StyledFooter,
  StyledHeader,
  StyledSection,
  StyledStack,
} from '@eneco/custom-components/flows/Layout';
import { Box, Form, SkipToMain, Stack } from '@eneco/sparky';
import { SpinnerIcon } from '@eneco/sparky/icons';
import { FieldValues, SubmitHandler, UseFormHandleSubmit } from 'react-hook-form';

const BODY_CLASS_NAME = 'flow-background';

interface CompoundLayoutProps {
  Header: typeof Header;
  Content: typeof Content;
  Footer: typeof Footer;
}

interface ContentProps {
  variant?: 'A' | 'B' | 'C' | 'D';
  isLoading?: boolean;
  handleSubmit?: UseFormHandleSubmit<FieldValues>;
  onSuccessfulSubmit?: SubmitHandler<FieldValues>;
  withDelayedAnimation?: boolean;
}

interface FooterProps {
  isLoading?: boolean;
}

// Enter in a field submits the form before onBlur validation has run.
// Enter on the focused submit button is that button's own activation:
// focus already left the field, so the blur validation has happened.
const checkKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
  if (event.key !== 'Enter' || isFocusedSubmitButton(event.target)) return;

  event.preventDefault();
};

const isFocusedSubmitButton = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return false;

  const button = target.closest('button');

  return button instanceof HTMLButtonElement && button.type === 'submit' && !button.disabled;
};

const Header: FC<PropsWithChildren<unknown>> = ({ children }) => (
  <>
    <SkipToMain.SkipLink />
    <StyledHeader>
      <Box paddingX={{ initial: '4', md: '8' }}>{children}</Box>
    </StyledHeader>
  </>
);

const Content: FC<PropsWithChildren<ContentProps>> = ({
  variant = 'A',
  withDelayedAnimation,
  isLoading,
  handleSubmit,
  children,
  onSuccessfulSubmit = () => undefined,
}) => (
  <SkipToMain.Main>
    {isLoading ? (
      <SpinnerIconContainer alignY="center" alignX="center">
        <Box paddingBottom="32">
          <SpinnerIcon size="large" color="iconBrand" />
        </Box>
      </SpinnerIconContainer>
    ) : (
      <StyledSection variant={variant} withDelayedAnimation={withDelayedAnimation}>
        <Box
          paddingY={{ initial: '8', md: '12', xl: '16' }}
          paddingX={variant === 'A' ? { initial: '6', md: '0' } : { initial: '6', md: '10', lg: '0' }}>
          <Form onSubmit={handleSubmit?.(onSuccessfulSubmit)} onKeyDown={e => checkKeyDown(e)}>
            <Stack direction="column" gap="6">
              {children}
            </Stack>
          </Form>
        </Box>
      </StyledSection>
    )}
  </SkipToMain.Main>
);

const Footer: FC<PropsWithChildren<FooterProps>> = ({ isLoading, children }) => (
  <StyledFooter isLoading={isLoading}>
    <Box paddingX={{ initial: '6', md: '10', lg: '0' }}>{children}</Box>
  </StyledFooter>
);

export const Layout: FC<PropsWithChildren<unknown>> & CompoundLayoutProps = ({ children }) => {
  useEffect(() => {
    setBodyBackgroundColor(BODY_CLASS_NAME);

    document.body.classList.add(BODY_CLASS_NAME);
    return () => document.body.classList.remove(BODY_CLASS_NAME);
  }, []);

  return <StyledStack direction="column">{children}</StyledStack>;
};

Layout.Header = Header;
Layout.Content = Content;
Layout.Footer = Footer;
